import { useQuery } from '@tanstack/react-query';
import { User } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useAuth } from './useAuth';

export type UserRole = 'client' | 'staff' | 'admin';

export type UserDoc = {
	displayName?: string | null;
	companyName?: string | null;
	role: UserRole;
	createdAt?: string | null;
};

export type UserType = User &
	UserDoc & {
		isLoading: boolean;
		isAdmin: boolean;
		isStaff: boolean;
		isClient: boolean;
		uid?: string;
	};

async function fetchUserDoc(uid: string): Promise<UserDoc | null> {
	try {
		const db = getFirestore();
		const userRef = doc(db, 'users', uid);
		const userSnap = await getDoc(userRef);

		if (!userSnap.exists()) return null;

		const data = userSnap.data();
		return {
			displayName: data.displayName ?? null,
			companyName: data.companyName ?? null,
			role: data.role ?? 'client',
			createdAt: data.createdAt ?? null,
		};
	} catch (error) {
		console.error('Error fetching user document:', error);
		return null;
	}
}

export const useUser = () => {
	const { user, loading } = useAuth();

	// Fetch additional user data from Firestore
	const { data: userDoc, isLoading: isLoadingUserDoc } = useQuery<UserDoc | null, Error>({
		queryKey: ['user', user?.uid],
		queryFn: () => fetchUserDoc(user?.uid!),
		enabled: !!user?.uid,
	});

	const userRole: UserRole = userDoc && userDoc.role ? userDoc.role : 'client';

	const mergedUser: UserType = {
		isLoading: isLoadingUserDoc || loading,
		role: userRole,
		...(user as User),
		...userDoc,
		uid: user?.uid || '',
		isAdmin: userDoc ? userDoc.role === 'admin' : false,
		isStaff: userDoc ? userDoc.role === 'staff' || userDoc.role === 'admin' : false,
		isClient: userDoc ? userDoc.role === 'client' : true, // Default to client
		emailVerified: user?.emailVerified ?? false,
	};

	return mergedUser;
};
